/**
 * 下次注意事項(REQ-20260918002)—— 前後端共用 schema。
 *
 * 「這次先這樣、下次要改正」的交代:印刷顏色要調、紙張要加厚、
 * 這次的特價下次下單要恢復原價。客戶與廠商各一張表(見 schema.prisma 該段註解),
 * 但 body 不含 owner FK(owner 由路徑決定),所以兩邊共用同一組 schema ——
 * 慣例同 supplier-interview.schema.ts 沿用 CreateInterviewSchema。
 */
import { z } from 'zod';

/**
 * 內容上限 500 字。這是一句交代不是一篇紀錄 ——
 * 要寫長篇的請寫進訪談 notes(DB 欄位是 TEXT,限制純粹是介面語意)。
 */
export const REMINDER_CONTENT_MAX = 500;

/** 記事商品 / 記要要點的上限,對齊 DB 欄長(REQ-20260922001)。 */
export const REMINDER_PRODUCT_MAX = 120;
export const REMINDER_KEY_POINT_MAX = 60;

/**
 * 記事日期是 date-only(YYYY-MM-DD),不含時間、不含時區 ——
 * 慣例逐字比照 inventory-record.schema.ts 的 `recordedAt`。
 * 用 `z.string().datetime()` 會逼前端送出帶時區的時間點,而它記的是「哪一天」。
 */
const noteDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'noteDate 格式須為 YYYY-MM-DD');

/**
 * 三個分類欄位都是選填(REQ-20260922001):只寫一句交代的人不該被逼著填商品與要點。
 * 空字串 → null:`''` 與「沒填」是同一件事,不要讓 DB 裡兩種都存在。
 */
const productNameSchema = z
  .string()
  .trim()
  .max(REMINDER_PRODUCT_MAX, `記事商品不可超過 ${REMINDER_PRODUCT_MAX} 字`)
  .transform((v) => (v.length === 0 ? null : v));

const keyPointSchema = z
  .string()
  .trim()
  .max(REMINDER_KEY_POINT_MAX, `記要要點不可超過 ${REMINDER_KEY_POINT_MAX} 字`)
  .transform((v) => (v.length === 0 ? null : v));

export const CreateReminderSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, '請輸入提醒內容')
    .max(REMINDER_CONTENT_MAX, `提醒內容不可超過 ${REMINDER_CONTENT_MAX} 字`),
  noteDate: noteDateSchema.nullable().optional(),
  productName: productNameSchema.nullable().optional(),
  keyPoint: keyPointSchema.nullable().optional(),
});
export type CreateReminderInput = z.infer<typeof CreateReminderSchema>;

/**
 * 編輯內容與勾完成走同一支 PATCH,兩格都是選填:
 * 只改內容時不帶 `done`,只按完成時不帶 `content`。
 */
export const UpdateReminderSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, '請輸入提醒內容')
    .max(REMINDER_CONTENT_MAX, `提醒內容不可超過 ${REMINDER_CONTENT_MAX} 字`)
    .optional(),
  noteDate: noteDateSchema.nullable().optional(),
  productName: productNameSchema.nullable().optional(),
  keyPoint: keyPointSchema.nullable().optional(),
  /** true = 標記完成(寫 doneAt / doneById);false = 復原成未完成(清回 null) */
  done: z.boolean().optional(),
});
export type UpdateReminderInput = z.infer<typeof UpdateReminderSchema>;

export const ReminderUserBriefSchema = z.object({
  id: z.number().int().positive(),
  displayName: z.string(),
});

export const ReminderSchema = z.object({
  id: z.number().int().positive(),
  content: z.string(),
  /** 三個分類欄位:舊資料(REQ-20260918002 時期)一律 null,不回填 */
  noteDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  productName: z.string().nullable(),
  keyPoint: z.string().nullable(),
  /** 非 null = 已完成 */
  doneAt: z.string().datetime().nullable(),
  doneBy: ReminderUserBriefSchema.nullable(),
  createdBy: ReminderUserBriefSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ReminderResponse = z.infer<typeof ReminderSchema>;

export const REMINDER_STATUSES = ['open', 'done', 'all'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];

/**
 * 預設 `open` —— 這個功能的重點就是「還沒做的那幾件」,
 * 單據上的提醒列與分頁徽章都只要這一段,讓最常見的呼叫不必帶參數。
 */
export const ReminderListQuerySchema = z.object({
  status: z.enum(REMINDER_STATUSES).default('open'),
});
export type ReminderListQuery = z.infer<typeof ReminderListQuerySchema>;
